/* FAQsPanel — admin CRUD for the 100+ FAQ entries served on /faqs.
 * Backed by /api/admin/faqs (GET/POST/PATCH/DELETE) + /api/admin/faqs/reorder.
 * Entries live in `db.faqs`; the initial seed comes from faqs_seed.FAQS_100
 * but from that point on every add / edit / reorder / delete is persisted.
 *
 * UX: inline editing with autosave on blur, up/down arrows for reordering
 * (batches into a single reorder call), and an "Add new FAQ" row at the
 * bottom. The {FREE} token is documented at the top; it's interpolated by
 * the public /faqs page against the current free-recipe limit.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { FloppyDisk, Trash, Plus, ArrowUp, ArrowDown, CaretDown, CaretUp, ArrowSquareOut } from "@phosphor-icons/react";
import { FAQ_CATEGORIES } from "@/lib/faqCategories";
import Media, { isVideoUrl } from "@/components/Media";
import ImageAnnotator from "@/components/ImageAnnotator";

// Up to 4 step pictures / videos per FAQ, each with an optional caption.
function FaqMediaEditor({ fid, media, onChange }) {
  const [busy, setBusy] = useState(false);
  const [annotIdx, setAnnotIdx] = useState(null);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Please keep uploads under 10 MB."); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange([...media, { url: data.url, caption: "" }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`faq-media-editor-${fid}`}>
      <label className="overline block mb-1">Pictures or videos (up to 4)</label>
      <div className="grid sm:grid-cols-2 gap-3">
        {media.map((m, i) => (
          <div key={m.url + i} className="rounded-xl border border-rf-ink/10 bg-white p-2 space-y-2" data-testid={`faq-media-item-${fid}-${i}`}>
            <Media src={m.url} alt="" className="w-full h-32 object-contain bg-rf-cream-alt rounded-lg" videoProps={{ controls: true, autoPlay: false }} />
            <input className="input-rf w-full !py-1.5 text-sm" placeholder="Caption (optional) — e.g. Tap New cookbook"
              value={m.caption || ""} data-testid={`faq-media-caption-${fid}-${i}`}
              onChange={(e) => onChange(media.map((x, j) => (j === i ? { ...x, caption: e.target.value } : x)))} />
            <div className="flex gap-3">
              {!isVideoUrl(m.url) && (
                <button type="button" className="text-xs font-bold text-rf-orange hover:underline" data-testid={`faq-media-highlight-${fid}-${i}`}
                  onClick={() => setAnnotIdx(i)}>Highlight</button>
              )}
              <button type="button" className="text-xs text-red-700 hover:underline" data-testid={`faq-media-remove-${fid}-${i}`}
                onClick={() => onChange(media.filter((_, j) => j !== i))}>Remove</button>
            </div>
          </div>
        ))}
      </div>
      {media.length < 4 && (
        <label className={`btn-outline-ink !py-2 !px-3 text-sm inline-flex mt-2 cursor-pointer ${busy ? "opacity-60 pointer-events-none" : ""}`}>
          {busy ? "Uploading…" : "+ Add picture or video"}
          <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
            data-testid={`faq-media-input-${fid}`} onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
      )}
      <p className="text-xs text-rf-ink-soft mt-1">JPG, PNG, WEBP, GIF or MP4/WebM/MOV, under 10 MB. Use Highlight to add arrows, circles, boxes or labels. Remember to Save.</p>
      <ImageAnnotator
        open={annotIdx !== null}
        src={annotIdx !== null ? media[annotIdx]?.url : ""}
        onClose={() => setAnnotIdx(null)}
        onSaved={(url) => onChange(media.map((x, j) => (j === annotIdx ? { ...x, url } : x)))}
      />
    </div>
  );
}

export default function FAQsPanel() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set()); // faq_ids currently open for editing
  const [drafts, setDrafts] = useState({}); // { [faq_id]: {question, answer} } — pending edits
  const [newQ, setNewQ] = useState("");
  const [newA, setNewA] = useState("");
  const [newCat, setNewCat] = useState("");
  const [saving, setSaving] = useState(new Set());
  const [sortMode, setSortMode] = useState("order");
  const [feedback, setFeedback] = useState({}); // { [faq_id]: [{reason, created_at}] }
  const loadFeedback = async (fid) => {
    const { data } = await api.get(`/admin/faqs/${fid}/feedback`);
    setFeedback((f) => ({ ...f, [fid]: data.feedback || [] }));
  };
  const helpScore = (f) => { const y = f.helpful_yes || 0; const n = f.helpful_no || 0; return y + n ? y / (y + n) : 2; };
  const shown = sortMode === "least"
    ? [...faqs].sort((a, b) => helpScore(a) - helpScore(b) || (b.helpful_no || 0) - (a.helpful_no || 0))
    : faqs;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/faqs");
      setFaqs(data.faqs || []);
    } catch {
      toast.error("Couldn't load FAQs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (fid) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(fid) ? next.delete(fid) : next.add(fid);
      return next;
    });
    setDrafts((d) => ({ ...d, [fid]: d[fid] || undefined }));
  };

  const setDraft = (fid, key, value) => {
    setDrafts((d) => {
      const current = d[fid] || {};
      const original = faqs.find((f) => f.faq_id === fid) || {};
      return { ...d, [fid]: { question: original.question, answer: original.answer, category: original.category, media: original.media || [],
        seo_title: original.seo_title || "", seo_description: original.seo_description || "", seo_keywords: original.seo_keywords || "", ...current, [key]: value } };
    });
  };

  const saveFaq = async (fid) => {
    const draft = drafts[fid];
    if (!draft) return;
    setSaving((s) => new Set(s).add(fid));
    try {
      const seo = { seo_title: draft.seo_title || "", seo_description: draft.seo_description || "", seo_keywords: draft.seo_keywords || "" };
      await api.patch(`/admin/faqs/${fid}`, { question: draft.question, answer: draft.answer, media: draft.media || [], ...seo, ...(draft.category ? { category: draft.category } : {}) });
      toast.success("Saved");
      setFaqs((prev) => prev.map((f) => f.faq_id === fid ? { ...f, question: draft.question, answer: draft.answer, media: draft.media || [], ...seo, category: draft.category || f.category } : f));
      setDrafts((d) => { const copy = { ...d }; delete copy[fid]; return copy; });
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSaving((s) => { const next = new Set(s); next.delete(fid); return next; });
    }
  };

  const deleteFaq = async (fid) => {
    if (!window.confirm("Delete this FAQ? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/faqs/${fid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= faqs.length) return;
    // Optimistic: swap locally, then send full order to the server so
    // it can compact contiguous integer order values.
    const next = [...faqs];
    [next[idx], next[target]] = [next[target], next[idx]];
    setFaqs(next);
    try {
      await api.post("/admin/faqs/reorder", { order: next.map((f) => f.faq_id) });
    } catch (e) {
      toast.error(`Reorder failed: ${e?.response?.data?.detail || e.message}`);
      load(); // revert on failure
    }
  };

  const createFaq = async () => {
    const q = newQ.trim();
    const a = newA.trim();
    if (!q || !a) {
      toast.error("Question and answer are both required");
      return;
    }
    try {
      const { data } = await api.post("/admin/faqs", { question: q, answer: a, ...(newCat ? { category: newCat } : {}) });
      toast.success("FAQ added");
      setNewQ(""); setNewA(""); setNewCat("");
      setFaqs((prev) => [...prev, data.faq]);
      setExpanded((prev) => new Set(prev).add(data.faq.faq_id));
    } catch (e) {
      toast.error(`Create failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-rf-ink-soft" data-testid="faqs-panel-loading">Loading FAQs…</div>
    );
  }

  return (
    <div className="space-y-4" data-testid="faqs-panel">
      <div className="card-recipe">
        <p className="overline mb-2">FAQ manager</p>
        <h2 className="font-display font-black text-2xl text-rf-ink mb-2">Edit the {faqs.length} FAQ entries served on /faqs</h2>
        <p className="text-sm text-rf-ink-soft">
          Every question / answer below appears on the public FAQ page under its heading, in the order shown here. Each one also has its own page for Google — use <b>View page</b> to open it. Use ↑ / ↓ to reorder, click a row to edit inline, and use the form at the bottom to add a new one. The <span className="font-mono bg-rf-cream-alt px-1.5 py-0.5 rounded text-xs">{`{FREE}`}</span> token in an answer will render the current free-recipe limit when the page is viewed.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm font-bold text-rf-ink">Sort</label>
        <select value={sortMode} onChange={(e) => setSortMode(e.target.value)} className="input-rf !w-auto !py-2" data-testid="faq-sort-select">
          <option value="order">Page order (use arrows to reorder)</option>
          <option value="least">Least helpful first (from visitor votes)</option>
        </select>
      </div>

      <div className="space-y-2" data-testid="faqs-list">
        {shown.map((f, idx) => {
          const isOpen = expanded.has(f.faq_id);
          const draft = drafts[f.faq_id];
          const dirty = !!draft && (draft.question !== f.question || draft.answer !== f.answer || (draft.category || f.category) !== f.category
            || JSON.stringify(draft.media || []) !== JSON.stringify(f.media || [])
            || ["seo_title", "seo_description", "seo_keywords"].some((k) => (draft[k] || "") !== (f[k] || "")));
          const yes = f.helpful_yes || 0; const no = f.helpful_no || 0;
          const isSaving = saving.has(f.faq_id);
          return (
            <div key={f.faq_id} className="card-recipe !p-4" data-testid={`faq-row-${f.faq_id}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0 || sortMode !== "order"}
                    className="p-1 rounded-md text-rf-ink-soft hover:bg-rf-amber/20 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`faq-up-${f.faq_id}`}
                    title="Move up"
                  ><ArrowUp size={14} weight="bold" /></button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === faqs.length - 1 || sortMode !== "order"}
                    className="p-1 rounded-md text-rf-ink-soft hover:bg-rf-amber/20 disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`faq-down-${f.faq_id}`}
                    title="Move down"
                  ><ArrowDown size={14} weight="bold" /></button>
                </div>
                <button
                  type="button"
                  onClick={() => toggle(f.faq_id)}
                  className="flex-1 text-left"
                  data-testid={`faq-toggle-${f.faq_id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-rf-orange bg-rf-amber/20 rounded-md px-1.5 py-0.5">#{faqs.indexOf(f) + 1}</span>
                    <p className="font-display font-extrabold text-rf-ink flex-1">{f.question}</p>
                    {isOpen ? <CaretUp size={16} weight="bold" className="text-rf-ink-soft" /> : <CaretDown size={16} weight="bold" className="text-rf-ink-soft" />}
                  </div>
                  {!isOpen && (
                    <p className="text-xs text-rf-ink-soft mt-1 line-clamp-2 pl-9">{f.answer}</p>
                  )}
                </button>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  {f.category && <span className="chip !text-[10px]" data-testid={`faq-category-chip-${f.faq_id}`}>{f.category}</span>}
                  <span className="text-[11px] font-bold text-rf-ink-soft whitespace-nowrap" data-testid={`faq-votes-${f.faq_id}`} title="Visitor votes on 'Was this helpful?'">
                    <span className="text-emerald-700">Yes {yes}</span> · <span className="text-red-700">No {no}</span>
                    {yes + no > 0 && <> · {Math.round((yes / (yes + no)) * 100)}%</>}
                  </span>
                  {f.slug && (
                    <a href={`/faqs/${f.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`faq-view-page-${f.faq_id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold text-rf-orange hover:underline" title={`/faqs/${f.slug}`}>
                      View page <ArrowSquareOut size={13} weight="bold" />
                    </a>
                  )}
                </div>
              </div>
              {isOpen && (
                <div className="mt-3 pl-9 space-y-3">
                  <div>
                    <label className="overline block mb-1">Question</label>
                    <input
                      data-testid={`faq-question-${f.faq_id}`}
                      className="input-rf w-full"
                      value={draft?.question ?? f.question}
                      onChange={(e) => setDraft(f.faq_id, "question", e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="overline block mb-1">Heading</label>
                    <select
                      data-testid={`faq-category-${f.faq_id}`}
                      className="input-rf w-full"
                      value={draft?.category ?? f.category ?? ""}
                      onChange={(e) => setDraft(f.faq_id, "category", e.target.value)}
                    >
                      {FAQ_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline block mb-1">Answer</label>
                    <textarea
                      data-testid={`faq-answer-${f.faq_id}`}
                      className="input-rf w-full min-h-[110px]"
                      value={draft?.answer ?? f.answer}
                      onChange={(e) => setDraft(f.faq_id, "answer", e.target.value)}
                    />
                  </div>
                  <div className="rounded-xl bg-rf-cream-alt p-3 space-y-2" data-testid={`faq-seo-${f.faq_id}`}>
                    <p className="overline">SEO for this FAQ&apos;s page (leave blank for automatic)</p>
                    <label className="block text-[11px] font-bold text-rf-ink-soft uppercase">Page title</label>
                    <input className="input-rf w-full" placeholder={`Title — automatic: "${f.question} — Recipe Filer FAQ"`} maxLength={120}
                      data-testid={`faq-seo-title-${f.faq_id}`} value={draft?.seo_title ?? f.seo_title ?? ""} onChange={(e) => setDraft(f.faq_id, "seo_title", e.target.value)} />
                    <label className="block text-[11px] font-bold text-rf-ink-soft uppercase">SEO description</label>
                    <textarea className="input-rf w-full min-h-[70px]" placeholder="Description — automatic: the first ~155 characters of the answer" maxLength={300}
                      data-testid={`faq-seo-description-${f.faq_id}`} value={draft?.seo_description ?? f.seo_description ?? ""} onChange={(e) => setDraft(f.faq_id, "seo_description", e.target.value)} />
                    <label className="block text-[11px] font-bold text-rf-ink-soft uppercase">Keywords</label>
                    <input className="input-rf w-full" placeholder="Keywords, comma separated — automatic from the question + heading" maxLength={400}
                      data-testid={`faq-seo-keywords-${f.faq_id}`} value={draft?.seo_keywords ?? f.seo_keywords ?? ""} onChange={(e) => setDraft(f.faq_id, "seo_keywords", e.target.value)} />
                    <p className="text-[11px] text-rf-ink-soft">Title {(draft?.seo_title ?? f.seo_title ?? "").length}/60 recommended · Description {(draft?.seo_description ?? f.seo_description ?? "").length}/155 recommended</p>
                  </div>
                  {no > 0 && (
                    <div data-testid={`faq-feedback-${f.faq_id}`}>
                      {!feedback[f.faq_id] ? (
                        <button type="button" className="text-xs font-bold text-rf-orange hover:underline" onClick={() => loadFeedback(f.faq_id)}
                          data-testid={`faq-feedback-load-${f.faq_id}`}>See what visitors said was missing</button>
                      ) : feedback[f.faq_id].length === 0 ? (
                        <p className="text-xs text-rf-ink-soft">No written comments yet.</p>
                      ) : (
                        <ul className="space-y-1 text-sm">
                          {feedback[f.faq_id].map((r, i) => (
                            <li key={i} className="rounded-lg bg-white border border-rf-ink/10 px-3 py-2">“{r.reason}” <span className="text-[11px] text-rf-ink-soft">{(r.created_at || "").slice(0, 10)}</span></li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                  <FaqMediaEditor fid={f.faq_id} media={draft?.media ?? f.media ?? []} onChange={(m) => setDraft(f.faq_id, "media", m)} />
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => deleteFaq(f.faq_id)}
                      className="btn-outline-ink !text-red-700 !border-red-300 hover:!bg-red-50 inline-flex items-center gap-1.5 text-sm !py-2 !px-3"
                      data-testid={`faq-delete-${f.faq_id}`}
                    >
                      <Trash size={14} weight="bold" /> Delete
                    </button>
                    <button
                      type="button"
                      onClick={() => saveFaq(f.faq_id)}
                      disabled={!dirty || isSaving}
                      className="btn-amber inline-flex items-center gap-1.5 text-sm !py-2 !px-3 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`faq-save-${f.faq_id}`}
                    >
                      <FloppyDisk size={14} weight="bold" /> {isSaving ? "Saving…" : dirty ? "Save changes" : "Saved"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card-recipe" data-testid="faq-add-form">
        <p className="overline mb-3">Add a new FAQ</p>
        <div className="space-y-3">
          <input
            data-testid="faq-new-question"
            className="input-rf w-full"
            placeholder="Question — e.g. 'Can I import from Notion?'"
            value={newQ}
            onChange={(e) => setNewQ(e.target.value)}
          />
          <textarea
            data-testid="faq-new-answer"
            className="input-rf w-full min-h-[110px]"
            placeholder={`Answer — Markdown-safe. Use {FREE} to interpolate the free-recipe count.`}
            value={newA}
            onChange={(e) => setNewA(e.target.value)}
          />
          <select data-testid="faq-new-category" className="input-rf w-full" value={newCat} onChange={(e) => setNewCat(e.target.value)}>
            <option value="">Heading — pick one, or leave to auto-choose</option>
            {FAQ_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={createFaq}
              className="btn-amber inline-flex items-center gap-1.5"
              data-testid="faq-create-btn"
            >
              <Plus size={16} weight="bold" /> Add FAQ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
